<template>
  <div class="flex flex-col items-center space-y-6 py-[70px] rounded-lg">
    <!-- Zone d'enregistrement principal -->
    <div style="transform: scale(1.5)" class="relative flex items-center justify-center">
      <!-- Halo animé -->
      <div
        :class="[
          'absolute rounded-full transition-all duration-300 ease-in-out',
          isRecording ? 'animate-pulse' : '',
          recordingState === 'idle' ? 'bg-blue-100' :
          recordingState === 'recording' ? 'bg-red-100' : 'bg-green-100'
        ]"
        :style="{
          width: haloSize*2.2 + 'px',
          height: haloSize*2.2 + 'px',
          opacity: isRecording ? 0.8 : 0.4
        }"
      ></div>
      <!-- Bouton microphone -->
      <button
        @click="toggleRecording"
        :disabled="isUploading"
        :class="[
          'relative z-10 p-4 rounded-full transition-all duration-200 transform hover:scale-105',
          'focus:outline-none focus:ring-4 focus:ring-blue-300',
          recordingState === 'idle' ? 'bg-blue-500 hover:bg-blue-600' :
          recordingState === 'recording' ? 'bg-red-500 hover:bg-red-600' :
          'bg-green-500 hover:bg-green-600',
          isUploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        ]"
      >
        <i
          :class="[
            'text-white text-2xl transition-transform duration-200',
            currentIcon,
            isRecording ? 'animate-pulse' : ''
          ]"
        ></i>
      </button>
    </div>

    <!-- Messages d'état -->
    <div class="text-center space-y-2 mt-[60px]">
      <p :class="[
        'text-lg font-medium transition-colors duration-200',
        recordingState === 'idle' ? 'text-gray-700' :
        recordingState === 'recording' ? 'text-red-600' : 'text-green-600'
      ]">
        {{ currentMessage }}
      </p>

      <div v-if="recordingTime > 0" class="text-sm text-gray-500">
        Durée: {{ formatTime(recordingTime) }}
      </div>
    </div>

    <!-- Actions après enregistrement -->
    <div v-if="recordingState === 'recorded'" class="flex space-x-4">
      <button
        @click="resetRecording"
        class="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600
               transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
      >
        <i class="fas fa-redo-alt"></i>
        <span>Recommencer</span>
      </button>
    </div>

    <!-- Messages d'erreur -->
    <div v-if="error" class="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import {ref, computed, watch, onMounted, onUnmounted, readonly} from 'vue'
import { useLanguageStore } from '@/stores/language'

// Props
const props = defineProps({
  questionSlug: {
    type: String,
    required: true
  },
  maxDuration: {
    type: Number,
    default: 120 // 2 minutes par défaut
  }
})

// Emits
const emit = defineEmits(['recording-complete', 'recording-submitted'])

// Store
const store = useLanguageStore()

// États réactifs
const recordingState = ref('idle') // 'idle', 'recording', 'recorded'
const isRecording = ref(false)
const isPlaying = ref(false)
const isUploading = ref(false)
const error = ref('')
const recordingTime = ref(0)
const audioLevel = ref(0)
const haloSize = ref(50)

// Enregistrement audio
const mediaRecorder = ref(null)
const audioChunks = ref([])
const audioBlob = ref(null)
const audioUrl = ref('')
const audioElement = ref(null)
const stream = ref(null)
const analyserNode = ref(null)
const dataArray = ref(null)
const animationFrame = ref(null)

// Timers
const recordingTimer = ref(null)
const maxDurationTimer = ref(null)

// Computed
const currentIcon = computed(() => {
  if (recordingState.value === 'idle') return 'fas fa-microphone'
  if (recordingState.value === 'recording') return 'fas fa-stop-circle'
  return 'fas fa-check-circle'
})

const currentMessage = computed(() => {
  if (isUploading.value) return 'Envoi en cours...'
  if (recordingState.value === 'idle') return 'Touchez le micro et commencez à parler'
  if (recordingState.value === 'recording') return 'Cliquez pour arrêter'
  return 'Enregistrement terminé'
})

// Méthodes
const initializeAudio = async () => {
  try {
    stream.value = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100
      }
    })

    // Configuration MediaRecorder
    const options = {
      mimeType: 'audio/webm;codecs=opus'
    }

    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      options.mimeType = 'audio/webm'
    }
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      options.mimeType = 'audio/mp4'
    }

    mediaRecorder.value = new MediaRecorder(stream.value, options)

    // Configuration de l'analyseur audio pour l'amplitude
    const audioContext = new (window.AudioContext || window.webkitAudioContext)()
    const source = audioContext.createMediaStreamSource(stream.value)
    analyserNode.value = audioContext.createAnalyser()
    analyserNode.value.fftSize = 256
    source.connect(analyserNode.value)
    dataArray.value = new Uint8Array(analyserNode.value.frequencyBinCount)

    // Gestionnaires d'événements MediaRecorder
    mediaRecorder.value.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.value.push(event.data)
      }
    }

    mediaRecorder.value.onstop = () => {
      audioBlob.value = new Blob(audioChunks.value, { type: 'audio/webm' })
      audioUrl.value = URL.createObjectURL(audioBlob.value)
      recordingState.value = 'recorded'
      emit('recording-complete', audioBlob.value)
    }

  } catch (err) {
    console.error('Erreur d\'initialisation audio:', err)
    error.value = 'Impossible d\'accéder au microphone. Vérifiez les permissions.'
  }
}

const startRecording = () => {
  if (!mediaRecorder.value || recordingState.value !== 'idle') return

  try {
    audioChunks.value = []
    mediaRecorder.value.start()
    isRecording.value = true
    recordingState.value = 'recording'
    recordingTime.value = 0
    error.value = ''

    // Démarrer le timer
    recordingTimer.value = setInterval(() => {
      recordingTime.value += 1
    }, 1000)

    // Timer pour durée maximale
    maxDurationTimer.value = setTimeout(() => {
      stopRecording()
    }, props.maxDuration * 1000)

    // Analyser l'amplitude audio
    analyzeAudioLevel()

  } catch (err) {
    console.error('Erreur lors du démarrage:', err)
    error.value = 'Erreur lors du démarrage de l\'enregistrement'
    resetRecording()
  }
}

const stopRecording = () => {
  if (!mediaRecorder.value || !isRecording.value) return

  mediaRecorder.value.stop()
  isRecording.value = false

  clearInterval(recordingTimer.value)
  clearTimeout(maxDurationTimer.value)
  cancelAnimationFrame(animationFrame.value)

  haloSize.value = 50
  audioLevel.value = 0
}

const analyzeAudioLevel = () => {
  if (!analyserNode.value || !isRecording.value) return

  analyserNode.value.getByteFrequencyData(dataArray.value)

  // Calculer l'amplitude moyenne
  const average = dataArray.value.reduce((sum, value) => sum + value, 0) / dataArray.value.length
  const normalizedLevel = (average / 255) * 100

  audioLevel.value = Math.max(10, normalizedLevel)
  const clamped = Math.max(10, Math.min(audioLevel.value, 20));
  const normalized = (clamped - 10) / (20 - 10);
  const sensitive = Math.sqrt(normalized);
  haloSize.value = (5 + sensitive * (20 - 5)) *2 +50;

  animationFrame.value = requestAnimationFrame(analyzeAudioLevel)
}

const toggleRecording = () => {
  if (recordingState.value === 'idle') {
    startRecording()
  } else if (recordingState.value === 'recording') {
    stopRecording()
  }
}

const resetRecording = () => {
  if (audioElement.value) {
    audioElement.value.pause()
    audioElement.value = null
  }

  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value)
  }

  audioChunks.value = []
  audioBlob.value = null
  audioUrl.value = ''
  recordingState.value = 'idle'
  recordingTime.value = 0
  audioLevel.value = 0
  haloSize.value = 50
  isPlaying.value = false
  error.value = ''

  clearInterval(recordingTimer.value)
  clearTimeout(maxDurationTimer.value)
}

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const cleanup = () => {
  if (stream.value) {
    stream.value.getTracks().forEach(track => track.stop())
  }

  clearInterval(recordingTimer.value)
  clearTimeout(maxDurationTimer.value)
  cancelAnimationFrame(animationFrame.value)

  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value)
  }

  if (audioElement.value) {
    audioElement.value.pause()
  }
}

// NOUVELLES MÉTHODES PUBLIQUES POUR LE COMPOSANT PARENT
const forceStopRecording = () => {
  return new Promise((resolve) => {
    if (isRecording.value) {
      // Quand on arrête, on attend que MediaRecorder déclenche `onstop`
      mediaRecorder.value.onstop = () => {
        audioBlob.value = new Blob(audioChunks.value, { type: 'audio/webm' })
        audioUrl.value = URL.createObjectURL(audioBlob.value)
        recordingState.value = 'recorded'
        emit('recording-complete', audioBlob.value)

        resolve(audioBlob.value) // ⬅️ on résout la promesse ici
      }
      stopRecording()
    } else {
      resolve(audioBlob.value)
    }
  })
}

const getRecordingState = () => {
  return {
    state: recordingState.value,
    isRecording: isRecording.value,
    duration: recordingTime.value,
    hasRecording: !!audioBlob.value
  }
}

const forceReset = () => {
  resetRecording()
}

// Exposer les méthodes pour le composant parent
defineExpose({
  stopRecording: forceStopRecording,
  resetRecording: forceReset,
  getState: getRecordingState,
  // États en lecture seule
  isRecording: readonly(isRecording),
  recordingState: readonly(recordingState),
  recordingTime: readonly(recordingTime)
})

// Lifecycle
onMounted(async () => {
  await initializeAudio()
})

watch(() => props.questionSlug, (newSlug, oldSlug) => {
  if (newSlug !== oldSlug) {
    resetRecording();
  }
});

onUnmounted(() => {
  cleanup()
})
</script>
